import React, { useEffect } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import Card from "components/card";
import CardMenu from "components/card/CardMenu";
import Spinner from "components/spinner";
import Button from "components/button";
import SelectField from "components/fields/SelectField";
import SearchInputField from "components/fields/SearchInputField";

import {
  LuArrowUpWideNarrow,
  LuArrowDownWideNarrow,
  LuArrowUpDown,
} from "react-icons/lu";
import ColumnDropdown from "./ColumnDropdown";

export default function DataTable({
  title = "",
  columns,
  data = [],
  className = "",
  rightAccessory,
  pageSizeOptions = [10, 25, 50],
  totalPages,
  pagination,
  setPagination,
  setSearchText,
  searchText,
  searchPlaceholder = "Search ...",
  error,
  total,
  loading,
  isColumnDropdown = false,
  initialColumnVisibility,
}) {
  const [sorting, setSorting] = React.useState([]);
  const [columnVisibility, setColumnVisibility] = React.useState(
    initialColumnVisibility || {}
  );
  useEffect(() => {
    setColumnVisibility(initialColumnVisibility);
  }, [initialColumnVisibility]);

  const start = pagination.pageIndex * pagination.pageSize + 1;
  const end = Math.min((pagination.pageIndex + 1) * pagination.pageSize, total);
  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Card extra={`w-full h-full sm:overflow-auto p-4 ${className}`}>
      {(title || rightAccessory) && (
        <header className="relative flex items-center justify-between">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            {title}
          </div>
          {rightAccessory ?? <CardMenu />}
        </header>
      )}

      <div className="mt-2 flex items-start justify-between md:items-center ">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 md:text-sm">
          <span className="">Show :</span>
          <SelectField
            name="pageSize"
            options={pageSizeOptions.map((size) => ({
              value: size,
              label: size.toString(),
            }))}
            value={
              pageSizeOptions
                .map((size) => ({ value: size, label: size.toString() }))
                .find((opt) => opt.value === (pagination?.pageSize ?? 10)) ||
              null
            }
            onChange={(selected) => {
              const newSize = Number(selected?.value);
              setPagination?.((prev) => ({
                ...prev,
                pageSize: newSize,
                pageIndex: 0,
              }));
            }}
            placeholder="Select page size"
          />
          <span className="">Entries</span>
        </div>
        <div className="flex flex-col items-center justify-center  gap-2 gap-2 md:flex-row">
          {isColumnDropdown && <ColumnDropdown table={table} />}
          <SearchInputField
            id="local-search"
            placeholder={searchPlaceholder}
            value={searchText ?? ""}
            className="mt-0 lg:w-[300px]"
            onChange={(e) => setSearchText?.(e.target.value)}
            shortcut="Ctrl K"
          />
        </div>
      </div>
      <div className="mt-4 overflow-x-auto rounded-md border border-gray-200 dark:border-white/10">
        <table className="w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-white/5">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isSorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort?.() ?? true;

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      className={[
                        "border-b   px-4 pb-2 pt-4 text-start dark:border-white/10",
                        canSort ? "cursor-pointer select-none" : "",
                        // index === 0 ? "rounded-tl-md" : "",
                        index === headerGroup.headers.length - 1
                          ? " "
                          : "border-r",
                      ].join(" ")}
                    >
                      <div className="flex items-center justify-start text-xs font-bold text-gray-600 dark:text-white">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        <span
                          className={`ml-2 text-base ${
                            canSort
                              ? "opacity-100"
                              : "pointer-events-none opacity-0"
                          }`}
                          aria-hidden={!isSorted}
                        >
                          {isSorted === "asc" ? (
                            <LuArrowUpWideNarrow />
                          ) : isSorted === "desc" ? (
                            <LuArrowDownWideNarrow />
                          ) : (
                            <LuArrowUpDown />
                          )}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading && (
              <tr className="border-b border-gray-200 hover:bg-gray-50 dark:border-white/[0.1] dark:hover:bg-white/[0.05]">
                <td colSpan={table.getAllColumns().length} className="py-2.5">
                  <div className="flex w-full items-center justify-center">
                    <Spinner />
                  </div>
                </td>
              </tr>
            )}
            {error && (
              <tr className="border-b border-gray-200 dark:border-white/[0.1]">
                <td
                  colSpan={table.getAllColumns().length}
                  className="px-6 py-4"
                >
                  <div className="flex items-center justify-center">
                    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01M12 3C7.03 3 3 7.03 3 12s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9z"
                        />
                      </svg>
                      <span>{error}</span>
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  className={[
                    "cursor-pointer whitespace-nowrap hover:bg-gray-50 dark:hover:bg-white/5 ",
                    rowIndex === table.getRowModel().rows.length - 1
                      ? "rounded-b-md"
                      : "",
                  ].join(" ")}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <td
                      key={cell.id}
                      className={[
                        "border-b border-gray-200 px-4 py-3 text-sm font-medium text-navy-700 dark:border-white/10 dark:text-white",
                        cellIndex !== row.getVisibleCells().length - 1
                          ? "border-r border-gray-200 dark:border-white/10"
                          : "",
                      ].join(" ")}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  className="py-6 text-center text-gray-500 dark:text-gray-400"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-600 dark:text-white md:text-sm">
            Showing {start} to {end} of {total} entries
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600 dark:text-white md:text-sm">
            Page {pagination.pageIndex + 1} of {totalPages}
          </span>

          <Button
            className="py-[3px]"
            onClick={() => {
              if (setPagination && pagination) {
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: Math.max(prev.pageIndex - 1, 0),
                }));
              }
            }}
            disabled={!pagination || pagination.pageIndex === 0}
          >
            Prev
          </Button>

          <Button
            className="py-[3px]"
            onClick={() => {
              if (setPagination && pagination) {
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: prev.pageIndex + 1,
                }));
              }
            }}
            disabled={pagination.pageIndex >= totalPages - 1}
          >
            Next
          </Button>
        </div>
      </div>
    </Card>
  );
}
